import React, { useEffect, useRef } from "react";
import orangeDot from "../../../../utilities/images/store_plan_active/icon-orange-dot.svg";
import lineIcon from "../../../../utilities/images/store_plan_active/icon-line.svg";
import "./DeltaUpgradeModal.css";

const textOf = (value) => {
  if (value == null) return "";
  const text = String(value).trim();
  return text && text !== "null" ? text : "";
};

const rowKind = (row) => {
  const type = textOf(row.row_type || row.type).toLowerCase();
  if (type === "credit" || type === "covered" || type === "discount") return "credit";
  if (type === "total" || type === "pay") return "total";
  const label = textOf(row.label).toLowerCase();
  if (/cover|credit/.test(label)) return "credit";
  if (/you pay|total|payable/.test(label)) return "total";
  return "";
};

const planTitle = (card) => {
  const name = textOf(card.package_name || card.section_name || card.title || card.plan_name || card.name);
  const paid = textOf(card.amount_paid_text || card.amount_paid || card.paid_text || card.paid_amount || card.price_text);
  if (!paid) return name;
  return /paid/i.test(paid) ? `${name} · ${paid}` : `${name} · ${paid} paid`;
};

const planUsage = (card) => {
  const written = textOf(card.usage_note || card.usage_text || card.usage);
  if (written) return written;
  const used = Number(card.days_used ?? card.used_days);
  const total = Number(card.total_days ?? card.validity_days ?? card.plan_days);
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return "";
  const percent = Number(card.usage_percent ?? card.used_percent);
  const shown = Number.isFinite(percent) ? Math.round(percent) : Math.round((used / total) * 100);
  return `${used} of ${total} days used (${shown}%)`;
};

const planCovered = (card) =>
  textOf(
    card.covered_amount_text ||
      card.covered_text ||
      card.credit_amount_text ||
      card.remaining_amount_text ||
      card.balance_text ||
      card.covered_amount
  );

const rupee = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
};

const asRecord = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : null);

const normalizePlan = (card) => {
  if (!card || typeof card !== "object") return null;
  const title = planTitle(card);
  const covered = planCovered(card);
  const usage = planUsage(card);
  if (!title && !covered && !usage) return null;
  return { title, covered, usage };
};

const breakdownFromPayload = (payload) => {
  const rows = [];
  const targetLabel = [textOf(payload.target_label), textOf(payload.target_duration_label)]
    .filter(Boolean)
    .join(" ");
  const targetPrice = rupee(payload.target_price);
  if (targetLabel || targetPrice) {
    rows.push({ label: targetLabel || "All sections", amount: targetPrice, kind: "" });
  }
  const credit = Number(payload.delta_credit_rounded || payload.final_credit || 0);
  if (credit > 0) {
    rows.push({
      label: "Covered by your plan",
      amount: `-${rupee(credit)}`,
      kind: "credit",
    });
  }
  const pay = rupee(payload.delta_pay_rounded || payload.delta);
  if (pay) rows.push({ label: "You pay", amount: pay, kind: "total" });
  return rows;
};

const readHowItWorks = (data) => {
  const payload = asRecord(data?.deltaupgrade_payload) || {};
  const how =
    asRecord(data?.how_it_works) ||
    asRecord(data?.how_this_works) ||
    asRecord(data?.upgrade_card?.how_it_works) ||
    {};
  const rawPlans = how.your_plan_cards || how.plans || payload.entitlements || [];
  const planCards = (Array.isArray(rawPlans) ? rawPlans : []).map(normalizePlan).filter(Boolean);
  const rawRows = how.breakdown || how.price_breakdown || [];
  const breakdown = (Array.isArray(rawRows) && rawRows.length ? rawRows : breakdownFromPayload(payload)).map(
    (row) => ({
      label: textOf(row.label || row.title || row.name),
      amount: textOf(row.amount_text || row.amount || row.value || row.price),
      kind: rowKind(row),
    })
  ).filter((row) => row.label || row.amount);
  return {
    title: textOf(how.title) || "How this works",
    planTitle: textOf(how.your_plan_title || how.plan_title) || "Your Plan",
    planCards,
    infoNote: textOf(how.info_note || how.note || payload.info_note || payload.note),
    breakdownTitle: textOf(how.breakdown_title) || "Price Breakdown",
    breakdown,
    accessNote: textOf(
      how.access_note || how.footer_note || payload.access_note || payload.replacement_note
    ),
  };
};

const DeltaUpgradeModal = ({ isOpen, onClose, data }) => {
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const fit = () => {
      const sheet = sheetRef.current;
      if (!sheet) return;
      const available = window.innerHeight - 32;
      const needed = sheet.offsetHeight;
      const scale = needed > available ? available / needed : 1;
      sheet.style.transform = scale < 1 ? `scale(${scale})` : "";
    };
    const frame = window.requestAnimationFrame(fit);
    window.addEventListener("resize", fit);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", fit);
    };
  }, [isOpen, data]);

  if (!isOpen) return null;

  const view = readHowItWorks(data);

  return (
    <div className="delta-upgrade-modal__backdrop" onClick={onClose} role="presentation">
      <div
        ref={sheetRef}
        className="delta-upgrade-modal__sheet"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="delta-upgrade-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg className="delta-upgrade-modal__cross" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M3.2 3.2L12.8 12.8M12.8 3.2L3.2 12.8" />
          </svg>
        </button>

        <div
          className="delta-upgrade-modal__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delta-upgrade-title"
        >
          <h3 id="delta-upgrade-title" className="delta-upgrade-modal__title">
            {view.title}
          </h3>

          {view.planCards.length > 0 ? (
            <section className="delta-upgrade-modal__block">
              <h4>{view.planTitle}</h4>
              {view.planCards.map((card, index) => (
                <div key={index} className="delta-upgrade-modal__plan-card">
                  <div className="delta-upgrade-modal__plan-head">
                    <p>
                      <img src={orangeDot} alt="" />
                      <span>{card.title}</span>
                    </p>
                    {card.covered ? <strong>{card.covered}</strong> : null}
                  </div>
                  {card.usage ? <p className="delta-upgrade-modal__usage">{card.usage}</p> : null}
                </div>
              ))}
            </section>
          ) : null}

          {view.infoNote ? <p className="delta-upgrade-modal__info-note">{view.infoNote}</p> : null}

          {view.breakdown.length > 0 ? (
            <section className="delta-upgrade-modal__block">
              <h4>{view.breakdownTitle}</h4>
              <div className="delta-upgrade-modal__breakdown">
                {view.breakdown.map((row, index) => (
                  <div key={`${row.label}-${index}`}>
                    {index > 0 ? (
                      <div className="delta-upgrade-modal__line">
                        <img src={lineIcon} alt="" />
                      </div>
                    ) : null}
                    <div className={`delta-upgrade-modal__row delta-upgrade-modal__row--${row.kind || "plain"}`}>
                      <span>{row.label}</span>
                      <strong>{row.amount}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {view.accessNote ? <p className="delta-upgrade-modal__access-note">{view.accessNote}</p> : null}
        </div>
      </div>
    </div>
  );
};

export default DeltaUpgradeModal;
